import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography
} from '@mui/material'
import { NavLink } from 'react-router-dom'
import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import PageHeader from '../components/PageHeader'
import AIAgentChatWidget from '../components/AIAgentChatWidget'

// Beta-only gate for AI agent. Change before wider rollout.
const AI_AGENT_PIN_CODE = '8520'

export default function AIAgentChatPage() {
  const [t] = useTranslation('common')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const pinInvalidText = t('aiAgentPinInvalid', { defaultValue: 'Invalid PIN code.' })
  const pinLockedHintText = t('aiAgentPinLockedHint', {
    defaultValue: 'Enter the PIN code to unlock the AI agent.'
  })
  const pinDialogTitleText = t('aiAgentPinDialogTitle', {
    defaultValue: 'AI Agent PIN code'
  })
  const pinDialogDescriptionText = t('aiAgentPinDialogDescription', {
    defaultValue: 'Enter the PIN code to continue to the AI agent.'
  })
  const pinFieldLabelText = t('aiAgentPinFieldLabel', { defaultValue: 'PIN code' })
  const pinSubmitText = t('aiAgentPinSubmit', { defaultValue: 'Open AI agent' })
  const [pinCode, setPinCode] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [pinUnlocked, setPinUnlocked] = useState(false)

  const handleUnlock = (event: FormEvent) => {
    event.preventDefault()

    if (pinCode.trim() !== AI_AGENT_PIN_CODE) {
      setPinError(pinInvalidText)
      return
    }

    setPinUnlocked(true)
    setPinCode('')
    setPinError(null)
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        maxHeight: '100%',
        overflow: 'hidden',
        p: { xs: 0, md: 2 }
      }}
    >
      {!isMobile && (
        <PageHeader
          title={t('Sidebar.aiAgent', { defaultValue: 'AI-agentti' })}
          compact
          breadcrumbs={
            <Breadcrumbs>
              <Link component={NavLink} to="/">
                {t('Sidebar.frontpage', { defaultValue: 'Etusivu' })}
              </Link>
              <Typography>{t('Sidebar.aiAgent', { defaultValue: 'AI-agentti' })}</Typography>
            </Breadcrumbs>
          }
        />
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {pinUnlocked ? (
          <AIAgentChatWidget />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3
            }}
          >
            <Typography variant="body1" color="text.secondary" align="center">
              {pinLockedHintText}
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog
        open={!pinUnlocked}
        disableEscapeKeyDown
        fullWidth
        maxWidth="xs"
        aria-labelledby="ai-agent-pin-dialog-title"
      >
        <Box component="form" onSubmit={handleUnlock}>
          <DialogTitle id="ai-agent-pin-dialog-title">{pinDialogTitleText}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {pinDialogDescriptionText}
            </Typography>

            <TextField
              autoFocus
              fullWidth
              type="password"
              value={pinCode}
              onChange={(event) => {
                setPinCode(event.target.value)
                if (pinError) {
                  setPinError(null)
                }
              }}
              label={pinFieldLabelText}
              inputProps={{
                maxLength: 32
              }}
            />

            {pinError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {pinError}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="contained" disabled={pinCode.trim().length === 0}>
              {pinSubmitText}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  )
}
